/* Antes que los estilos del editor: sus reglas del teléfono (`19-cms-mobile`)
   la sobrescriben, como cuando vivía dentro de `12-cms.css`. */
import '@webbuilder/ui/block-editor.css'
import './styles/editor-v2.css'
import './styles/editor-compat.css'
import '@webbuilder/ui/sidebar-navigation.css'
import '@webbuilder/ui/brand.css'
import '@webbuilder/ui/loading-mascot.css'
import '@webbuilder/ui/ai-mascot.css'
import '@webbuilder/ui/account-menu.css'
import '@webbuilder/ui/record-fields.css'
import { applyStoredAccent } from '@webbuilder/ui'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { EditorCrashBoundary } from './features/editor-shell/EditorCrashBoundary'
import { runSignOut } from './signout'
import { EditorBoot } from './appStatusViews'
import { LanguageProvider, activeLanguage, loadEditorCatalog } from './lib/i18n'
import { installInputMode } from './lib/inputMode'
import { installBrowserChrome } from './lib/browserChrome'
import { installPhoneSheetStack } from './lib/phoneSheetStack'

const root = document.getElementById('root')
if (!root) throw new Error('#root element not found')

/* El acento elegido en el panel, aquí también: viaja en una cookie de
   `.veltica.com`, así que cruzar del panel al editor no cambia de color. El
   editor no tiene selector propio — se elige en un sitio. Ver `accent.ts`. */
applyStoredAccent()

/* Dedo o ratón, y qué pantalla: `data-input` y `data-form-factor` en <html>,
   antes del primer pintado para que el CSS táctil ya esté puesto. */
installInputMode()
// Y cuánto tapa la barra del navegador por abajo (ver `browserChrome.ts`).
installBrowserChrome()
// En el teléfono, las hojas apiladas son una sola ventana con «Volver».
installPhoneSheetStack()

/**
 * `?signout=1` — el dashboard rebota por aquí al cerrar sesión.
 *
 * Cada origen guarda su propia sesión, así que cerrarla en el dashboard no
 * tocaba la del editor: seguía autenticado con la cuenta anterior hasta que su
 * token caducara, y mientras tanto abrir un proyecto de la cuenta nueva daba
 * 403. Scope local porque la sesión del servidor ya la cerró quien nos mandó.
 */
const params = new URLSearchParams(window.location.search)
const wizardPreview = params.get('wizardPreview') === '1'
/* Lo que se va a pintar, pedido ya, a la par que los textos. Y el editor
   entero sólo cuando se va a abrir: la vista previa del asistente no lo usa, y
   estático lo descargaba igual —todas las pantallas, paneles y barras— antes de
   poder pintar nada. En local, sin empaquetar, eran miles de módulos y la vista
   previa tardaba en salir. */
const previewModule = wizardPreview ? import('./features/wizardPreview/wizardPreviewFrame') : null
const appModule = wizardPreview || params.get('signout') === '1' ? null : import('./App')
/* Los textos del editor en su idioma, antes de pintar nada (ver
   `loadEditorCatalog`). Si fallara la descarga, se sigue: saldrían claves en
   vez de textos, pero el editor abre. */
await loadEditorCatalog(activeLanguage()).catch(() => {})

if (previewModule) {
  /* La vista previa del asistente «crear con IA» del panel: sólo pinta la
     página con el render de publicar. Ver `wizardPreviewFrame`. */
  void previewModule.then(({ startWizardPreviewFrame }) => startWizardPreviewFrame(root))
} else if (params.get('signout') === '1') {
  // La misma pantalla de arranque mientras se limpia y se sale: el rebote es
  // corto, pero en blanco parecería que el editor se rompió.
  createRoot(root).render(<EditorBoot phase="redirecting" />)
  void runSignOut(params.get('returnTo'))
} else {
  const { default: App } = await (appModule ?? import('./App'))
  createRoot(root).render(
    <StrictMode>
      {/* El idioma envuelve todo, igual que en el panel: hay avisos y rótulos
          antes de que haya proyecto abierto. */}
      <LanguageProvider>
        {/* Un fallo al dibujar no deja la pantalla en blanco ni pierde lo hecho:
            ver `EditorCrashBoundary`. */}
        <EditorCrashBoundary>
          <App />
        </EditorCrashBoundary>
      </LanguageProvider>
    </StrictMode>
  )
}
